<script setup>
import { computed } from 'vue'
import { useRoute, createError, useHead, useNuxtApp } from '#imports'
import brandenburgLocations from '~/data/brandenburg'
import HeroSection from '~/components/landing/HeroSection.vue'
import EventTypesSection from '~/components/landing/EventTypesSection.vue'
import FeaturesSection from '~/components/landing/FeaturesSection.vue'
import GallerySection from '~/components/landing/GallerySection.vue'
import PackagesSection from '~/components/landing/PackagesSection.vue'
import FaqSection from '~/components/landing/FaqSection.vue'
import ContactForm from '~/components/landing/ContactForm.vue'
import ProcessSection from '~/components/landing/ProcessSection.vue'
import LocationSection from '~/components/landing/LocationSection.vue'
import SiteFooter from '~/components/SiteFooter.vue'
import { getDistrictEventTypes } from '~/data/districtEventTypes'

const route = useRoute()
const routeDistrict = Array.isArray(route.params.district) ? route.params.district[0] : route.params.district
const location = brandenburgLocations[String(routeDistrict || '')]

if (!routeDistrict || !location) {
  throw createError({ statusCode: 404, message: 'Location not found' })
}

const district = {
  slug: String(routeDistrict),
  name: location.name,
  description: location.description || location.title || `Professionelle Fotobox für Ihre Veranstaltung in ${location.name}.`,
  venues: location.venues?.slice(0, 11) || [],
  eventTypes: getDistrictEventTypes('brandenburg')
}

const pageTitle = `Fotobox mieten in ${district.name} | Alle Eventarten`
const pageDescription = district.description
const canonicalUrl = `https://mietfotobox.com/brandenburg/${district.slug}/`

useSeoMeta({
  title: pageTitle,
  description: pageDescription,
  ogTitle: pageTitle,
  ogDescription: pageDescription,
  ogImage: 'https://mietfotobox.com/static/assets/hero.webp',
  canonical: canonicalUrl
})

const { $generateBreadcrumbSchema } = useNuxtApp()

const breadcrumbData = computed(() =>
  $generateBreadcrumbSchema([
    {
      name: 'mietfotobox.com',
      item: 'https://mietfotobox.com/'
    },
    {
      name: 'Brandenburg',
      item: 'https://mietfotobox.com/brandenburg/'
    },
    {
      name: district.name,
      item: canonicalUrl
    }
  ])
)

useHead(() => ({
  script: breadcrumbData.value && Object.keys(breadcrumbData.value).length > 0
    ? [
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(breadcrumbData.value)
        }
      ]
    : []
}))

const faqItems = computed(() => [
  {
    question: `Welche Eventarten bieten Sie in ${district.name} an?`,
    answer: `In ${district.name} können Sie aktuell Anlass-Seiten für Hochzeit, Geburtstag, Firmenfeier und Jugendweihe direkt auswählen.`
  },
  {
    question: `Kommt die Fotobox auch direkt zu meiner Location in ${district.name}?`,
    answer: `Ja. Wir stimmen Lieferung, Aufbau und Abholung passend zu Ihrer Veranstaltung in ${district.name} mit Ihnen ab.`
  },
  {
    question: `Kann ich für ${district.name} sofort eine Anfrage stellen?`,
    answer: `Ja. Im Anfragebereich können Sie Ort, Anlass, Datum und Eckdaten direkt für ein individuelles Angebot übermitteln.`
  },
  {
    question: `Ist die Fotobox für verschiedene Locations in ${district.name} geeignet?`,
    answer: `Ja. Unsere Fotoboxen lassen sich flexibel in Hotels, Gaststätten, Eventlocations oder privaten Räumen einsetzen.`
  }
])

const locationDescription = computed(() => {
  return location.locationDescription || `${district.description} Wir helfen bei Planung, Aufstellung und der Auswahl des passenden Pakets für Ihre Veranstaltung.`
})
</script>

<template>
  <div>
    <HeroSection
      :title="`Fotobox mieten in ${district.name}`"
      :intro-text="`Wählen Sie für ${district.name} direkt den passenden Anlass.`"
      :hero-image="location.heroImage || 'https://mietfotobox.com/static/assets/hero.webp'"
    />

    <EventTypesSection
      :location-name="district.name"
      :base-path="`/brandenburg/${district.slug}`"
      :event-types="district.eventTypes"
      :venues="district.venues"
    />

    <FeaturesSection :location-name="district.name" eventType="Veranstaltung" />
    <GallerySection :location-name="district.name" />
    <PackagesSection :location-name="district.name" />
    <div id="inquiry-form" class="bg-pink-700 px-4"></div>
    <ContactForm />
    <FaqSection :faq-items="faqItems" :location-name="district.name" />
    <ProcessSection :location-name="district.name" />
    <LocationSection :location-name="district.name" eventType="Veranstaltungen" :description="locationDescription" />
    <SiteFooter :current-location="district.name" :neighbor-locations="location.neighborLocations || []" />
  </div>
</template> 
